import type { ReactNode } from 'react'
import { XIcon } from '../icons'
import './ConfirmDialog.css'

export interface ConfirmSpec {
  title: string
  body: ReactNode
  /** Label on the button that goes through with it. */
  confirmLabel: string
  /**
   * Geri dönmek düğme yerine sağ üstteki kırmızı çarpıyla (kullanıcı, 16 Eki
   * 2026): üç düğme yan yana sıkışıyordu. Alttaki iki düğme ikiye yayılıyor.
   */
  closeX?: boolean
  /** Red when the action removes something rather than adding it. */
  danger?: boolean
  /**
   * What to do on confirm. Omitted for a notice, something the cashier can
   * only acknowledge, like a waiter's round arriving mid-payment, which shows
   * a single button and no way to "confirm" anything.
   */
  onConfirm?: () => void
  /**
   * A second way through, for questions with two reasonable answers rather
   * than one. Emptying a table is the case: the cashier may want the table
   * closed, or may just want the items gone while the tab stays open.
   */
  altLabel?: string
  onAlt?: () => void
}

interface Props extends ConfirmSpec {
  onClose: () => void
}

/**
 * The app's own confirmation, replacing window.confirm and window.alert.
 *
 * The browser's dialogs arrive in whatever language the browser is set to,
 * name the site in the title bar, and cannot say "Masayı kapat" on the button
 * that closes a table. At a till, where the same three questions come up all
 * day, reading the wrong dialog is how the wrong table gets scrapped.
 */
export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  closeX = false,
  danger,
  onConfirm,
  altLabel,
  onAlt,
  onClose,
}: Props) {
  const notice = onConfirm === undefined

  return (
    <div className="dlg" role="dialog" aria-modal="true">
      <div className={`dlg__card dlg__card--ask ${closeX ? 'dlg__card--x' : ''}`}>
        {closeX && (
          <button type="button" className="dlg__x" onClick={onClose} aria-label="Kapat">
            <XIcon />
          </button>
        )}
        <h2>{title}</h2>
        <div className="dlg__empty">{body}</div>

        {/* Spread across the card rather than collected in the corner: these
            are hit with a thumb mid-service, and a small target at the edge is
            one a hand in a hurry misses. */}
        <div className={`dlg__buttons ${notice ? '' : 'dlg__buttons--even'}`}>
          {/* A notice has nothing to decline, so it gets one button and that
              button is the affirmative one. */}
          {!notice && !closeX && (
            <button type="button" className="btn btn--ghost" onClick={onClose}>
              Vazgeç
            </button>
          )}
          {/* Outlined, between the two: it is a real choice, but the one named
              in the title is still the one the dialog came to ask about. */}
          {altLabel && onAlt && (
            <button
              type="button"
              className="btn btn--outline"
              onClick={() => {
                onClose()
                onAlt()
              }}
            >
              {altLabel}
            </button>
          )}
          <button
            type="button"
            className={`btn ${danger ? 'btn--danger' : 'btn--primary'}`}
            onClick={() => {
              onClose()
              onConfirm?.()
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
